
import { useEffect, useRef } from 'react';
import { GameStatistics, GameState } from '../contexts/GameContextTypes';
import { initialStatistics } from '../contexts/initialGameState';

const STORAGE_KEY = 'bitcoin-mining-simulator-stats';

export const loadStatistics = (): GameStatistics => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...initialStatistics, ...parsed };
    }
  } catch (error) {
    console.error('Failed to load statistics:', error);
  }
  return initialStatistics;
};

export const saveStatistics = (stats: GameStatistics): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  } catch (error) {
    console.error('Failed to save statistics:', error);
  }
};

export const useStatisticsTracker = (
  gameState: GameState,
  updateStatistics: (stats: Partial<GameStatistics>) => void
) => {
  const sessionStartTime = useRef<number>(Date.now());
  const lastSaveTime = useRef<number>(Date.now());

  // Track session time and auto-save every 30 seconds
  useEffect(() => {
    if (!gameState.gameStarted) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const sessionDuration = Math.floor((now - lastSaveTime.current) / 1000);
      
      if (sessionDuration >= 30) {
        const totalTime = gameState.statistics.totalTimePlayed + sessionDuration;
        updateStatistics({ 
          totalTimePlayed: totalTime,
          lastPlayedDate: new Date().toISOString()
        });
        lastSaveTime.current = now;
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [gameState.gameStarted, gameState.statistics.totalTimePlayed, updateStatistics]);

  // Track role played on game start
  useEffect(() => {
    if (gameState.gameStarted && gameState.selectedRole) {
      const roleId = gameState.selectedRole.id;
      if (!gameState.statistics.rolesPlayed.includes(roleId)) {
        updateStatistics({
          rolesPlayed: [...gameState.statistics.rolesPlayed, roleId],
          sessionsPlayed: gameState.statistics.sessionsPlayed + 1,
        });
      }
    }
  }, [gameState.gameStarted, gameState.selectedRole]);

  // Track highest level achieved
  useEffect(() => {
    const levelOrder = ['Junior', 'Mid-Level', 'Senior', 'Leader', 'Expert'];
    const currentIndex = levelOrder.indexOf(gameState.careerLevel);
    const highestIndex = levelOrder.indexOf(gameState.statistics.highestLevel);
    
    if (currentIndex > highestIndex) {
      updateStatistics({ highestLevel: gameState.careerLevel });
    }
  }, [gameState.careerLevel]);

  // Track skills unlocked
  useEffect(() => {
    if (gameState.skills.length > gameState.statistics.skillsUnlocked) {
      updateStatistics({ skillsUnlocked: gameState.skills.length });
    }
  }, [gameState.skills.length]);

  // Track achievements earned
  useEffect(() => {
    if (gameState.achievements.length > gameState.statistics.achievementsEarned) {
      updateStatistics({ achievementsEarned: gameState.achievements.length });
    }
  }, [gameState.achievements.length]);

  // Save on page unload
  useEffect(() => {
    const handleUnload = () => {
      const sessionDuration = Math.floor((Date.now() - sessionStartTime.current) / 1000);
      const finalStats = {
        ...gameState.statistics,
        totalTimePlayed: gameState.statistics.totalTimePlayed + sessionDuration,
        lastPlayedDate: new Date().toISOString(),
      };
      saveStatistics(finalStats);
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [gameState.statistics]);

  return { sessionStartTime };
};
